import { useEffect, useState, useRef, Fragment } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { get, put, post } from "util/requests";
import styles from "./crf_builder.module.css";
import { cloneDeep } from "lodash";
import DomainSelectModal from "ui/modals/domain_select";
import RegionSelectModal from "ui/modals/region_select";
import EditableText from "./components/editable_text";
import FieldRow from "./components/field_row";
import LoadingButton from "@mui/lab/LoadingButton";

export default function CrfBuilder() {
  const { crf_id, version = "1.0" } = useParams();
  const [crf, setCrf] = useState(null);
  const [domains, setDomains] = useState([]);
  const [regions, setRegions] = useState([]);
  const [activeDomains, setActiveDomains] = useState([]);
  const [domainModalIsOpenData, setDomainModalIsOpenData] = useState(false);
  const [regionModalisOpenData, setRegionModalisOpenData] = useState(false);
  const study_id = useSelector((state) => state.study.active_study_id);
  const [lockedCrf, setLockedCrf] = useState(false);
  const [tabOverride, setTabOverride] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (crf_id) {
      get(`/crf_design/${study_id}/${crf_id}/${version}`)
        .then((ret) => {
          if (ret.data && ret.data.locked) {
            setLockedCrf(true);
          } else if ((ret.data && Object.keys(ret.data).length > 0) || (ret.data && ret.data.name)) {
            ret.data.design.fields = ret.data.design.fields.map((field) => ({
              ...field,
              key: crypto.randomUUID(),
            }));
            setCrf(ret.data);
            if (ret.data.domains) {
              setActiveDomains(ret.data.domains);
            }
          } else {
            setCrf({ design: { fields: [] } });
          }
        })
        .catch((err) => console.log(err));
      get(`/invite_user_sites_and_roles/${study_id}`).then(({ data }) => {
        setDomains(data.domains);
        setRegions(data.regions);
      });
    }
  }, []);

  const addField = () => {
    let _crf = { ...crf };
    if (!crf.design.fields || Object.keys(crf.design.fields).length === 0) {
      _crf.design = { fields: [{ id: 1, label: "new" }] };
    } else {
      _crf.design.fields.push({
        id: crf.design.fields.slice(-1)[0].id + 1,
        label: "new",
      });
    }
    setCrf(_crf);
  };
  const options = [
    { label: "Date", value: "date" },
    { label: "Date Time", value: "datetime" },
    { label: "Time", value: "time" },
    { label: "Radio", value: "radio" },
    { label: "Select", value: "select" },
    { label: "Int", value: "int" },
    { label: "Year", value: "year" },
    { label: "Decimal", value: "decimal" },
    { label: "Blood Pressure", value: "blood_pressure" },
    { label: "Heading", value: "heading" },
    { label: "Weight", value: "weight" },
    { label: "Text Field", value: "text" },
    { label: "BP Date Time", value: "blood_pressure_date_time" },
    { label: "Blood Test Result", value: "blood_test_result" },
    { label: "Add Another", value: "add_another" },
    { label: "Drop Down", value: "drop_down" },
  ];

  const changeName = (name) => {
    //changes the title of the crf
    let _crf = { ...crf };
    _crf.name = name;
    setCrf(_crf);
  };
  const changeLabel = (id, label) => {
    let _crf = { ...crf };
    let field = _crf.design.fields.find((f) => f.id === id);
    field.label = label;
    setCrf(_crf);
  };
  const changeType = (id, type) => {
    let _crf = { ...crf };
    let field = _crf.design.fields.find((f) => f.id === id);
    field.type = type;
    setCrf(_crf);
  };
  const changeVariableName = (id, variable_name, value) => {
    let _crf = { ...crf };
    let field = _crf.design.fields.find((f) => f.id === id);
    //variable name is stored in name attribute of each field
    field[variable_name] = value;
    setCrf(_crf);
  };

  const setOptions = (id, options) => {
    let _crf = cloneDeep(crf);

    let field = _crf.design.fields.find((f) => f.id === id);
    field.options = options;
    setCrf(_crf);
  };

  const updateField = (id, new_field) => {
    let _crf = cloneDeep(crf);
    // let _crf = crf;
    let field = _crf.design.fields.find((f) => f.id === id);
    Object.assign(field, new_field);
    setCrf(_crf);
  };

  const setReadOnly = (id, value) => {
    let _crf = cloneDeep(crf);
    let field = _crf.design.fields.find((f) => f.id === id);
    field.read_only = value;
    setCrf(_crf);
  };

  const setRequired = (id, value) => {
    let _crf = cloneDeep(crf);
    let field = _crf.design.fields.find((f) => f.id === id);
    field.required = value;
    setCrf(_crf);
  };

  const updatePermissions = (type, value) => {
    let _crf = cloneDeep(crf);
    if (type === "write") {
      _crf.write_permissions = value;
    } else {
      _crf.read_permissions = value;
    }
    setCrf(_crf);
  };
  const updateDomains = (id, domains) => {
    let _crf = cloneDeep(crf);
    let field = _crf.design.fields.find((f) => f.id === id);
    field.domains = domains;
    setCrf(_crf);
  };
  const updateRegions = (id, regions) => {
    let _crf = cloneDeep(crf);
    let field = _crf.design.fields.find((f) => f.id === id);
    field.regions = regions;
    setCrf(_crf);
  };
  const updateOptionsRegions = (id, option_index, regions) => {
    let _crf = cloneDeep(crf);
    let field = _crf.design.fields.find((f) => f.id === id);

    if (field.type === "drop_down") {
      field.options[option_index].regions = regions;
    } else {
      field.regions = regions;
    }
    setCrf(_crf);
  };

  const saveCrf = () => {
    crf.design.fields.forEach((f) => {
      if (f.display_logic?.rules?.length) {
        f.display_logic.rules = f.display_logic?.rules.filter((r) => r.field_id || r.logic_type === "multiple_or");
      }
    });
    setSaving(true);
    post("/crf_design", {
      crf_id,
      design: crf.design,
      crf,
      name: crf.name,
      study_id,
      version,
    }).then((ret) => {
      if (ret.data?.locked) {
        setLockedCrf(true);
      } else {
        setSaving(false);
      }
    });
  };

  const addAbove = (field_id) => {
    let _crf = cloneDeep(crf);
    _crf.design.fields.splice(field_id - 1, 0, {
      id: field_id,
      label: "new",
    });
    _crf.design.fields.forEach((field, index) => {
      field.id = index + 1;
    });
    setCrf(_crf);
  };

  const addBelow = (field_id) => {
    let _crf = cloneDeep(crf);
    _crf.design.fields.splice(field_id, 0, {
      id: field_id,
      label: "new",
    });
    _crf.design.fields.forEach((field, index) => {
      field.id = index + 1;
    });
    setCrf(_crf);
  };

  const removeField = (field_id) => {
    let _crf = cloneDeep(crf);
    // let _crf = crf;
    _crf.design.fields = _crf.design.fields.filter((field) => parseInt(field.id) !== parseInt(field_id));
    _crf.design.fields.forEach((field, index) => {
      field.id = index + 1;
    });
    setCrf(_crf);
  };

  const getTabClassName = (key) => {
    if (key === tabOverride) {
      return styles.force_tab_button_active;
    } else {
      return styles.force_tab_button;
    }
  };

  if (lockedCrf) {
    return <div> This CRF is locked. Please contact your admin to make changes.</div>;
  } else if (!crf) return null;
  return (
    <div className={styles.wrapper}>
      <div className={styles.crf_builder_title}>
        <div>
          <span className={styles.heading}>Form Title: </span>
        </div>
        <EditableText
          text={crf.name || "new"}
          onChange={(value) => {
            changeName(value);
          }}
        />
        <div className={styles.force_tab_buttons}>
          <div className={getTabClassName("data")} onClick={() => setTabOverride("data")}>
            Data
          </div>
          <div className={getTabClassName("options")} onClick={() => setTabOverride("options")}>
            Options
          </div>
          <div className={getTabClassName("config")} onClick={() => setTabOverride("config")}>
            Config
          </div>
          <div className={getTabClassName("logic")} onClick={() => setTabOverride("logic")}>
            Logic
          </div>
          <div className={getTabClassName("rules")} onClick={() => setTabOverride("rules")}>
            Rules
          </div>
        </div>
      </div>

      {crf.design.fields &&
        crf.design.fields.map((field) => (
          <FieldRow
            field={field}
            options={options}
            changeType={changeType}
            changeLabel={changeLabel}
            changeVariableName={changeVariableName}
            addAbove={addAbove}
            addBelow={addBelow}
            removeField={removeField}
            updateField={updateField}
            setOptions={setOptions}
            setReadOnly={setReadOnly}
            setRequired={setRequired}
            setDomainModalIsOpenData={setDomainModalIsOpenData}
            setRegionModalisOpenData={setRegionModalisOpenData}
            regions={regions}
            tabOverride={tabOverride}
            setTabOverride={setTabOverride}
            fieldArray={crf.design.fields}
          />
        ))}
      {!crf.design?.fields || crf.design?.fields.length === 0 ? (
        <LoadingButton
          variant="outlined"
          loading={false}
          onClick={addField}
          sx={{
            fontSize: 14,
            padding: "2px",
            paddingLeft: "10px",
            paddingRight: "10px",
            marginLeft: "10px",
            width: "120px",
          }}
        >
          ADD FIELD
        </LoadingButton>
      ) : null}
      <div className={styles.spacer}>
        <br />
        <br />
        <br />
      </div>
      <div className={styles.footer}>
        <LoadingButton
          variant="outlined"
          loading={saving}
          onClick={saveCrf}
          sx={{
            fontSize: 14,
            padding: "2px",
            paddingLeft: "10px",
            paddingRight: "10px",
            marginLeft: "10px",
          }}
        >
          Save
        </LoadingButton>
      </div>
      <DomainSelectModal
        is_open={domainModalIsOpenData}
        title={"Domains"}
        activeDomains={domainModalIsOpenData.activeDomains}
        domainList={domains}
        close={() => setDomainModalIsOpenData(false)}
        updateParent={(domains) => updateDomains(domainModalIsOpenData.id, domains)}
      />
      <RegionSelectModal
        is_open={regionModalisOpenData}
        title={"Regions"}
        activeRegions={regionModalisOpenData.activeRegions}
        regionList={regions}
        close={() => setRegionModalisOpenData(false)}
        updateParent={(regions) => {
          if (regionModalisOpenData.source === "option") {
            updateOptionsRegions(regionModalisOpenData.id, regionModalisOpenData.option_index, regions);
          } else {
            updateRegions(regionModalisOpenData.id, regions);
          }
        }}
      />
    </div>
  );
}
